import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  Stack,
  Badge,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Mail as MailIcon,
  Add as AddIcon,
  Support as SupportIcon,
  Circle as CircleIcon,
  HourglassEmpty as HourglassIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { formatDistanceToNow } from 'date-fns';
import NewMessageModal from '../../components/messages/NewMessageModal';
import { useMessagePolling } from '../../hooks/useMessagePolling';
import { brandColors } from '../../theme';

interface MessageThread {
  id: number;
  subject: string;
  category: string | null;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  lastMessage: {
    content: string;
    senderType: 'user' | 'admin';
    createdAt: string;
  } | null;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
  isArchived?: boolean;
}

interface ThreadsResponse {
  success: boolean;
  data?: {
    threads: MessageThread[];
  };
}

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState<'inbox' | 'archived'>('inbox');
  const [newMessageModalOpen, setNewMessageModalOpen] = useState(false);

  // Enable polling for this page
  useMessagePolling({
    enabled: true,
    interval: 30000, // 30 seconds for thread list
    onMessagesPage: true,
  });

  // Fetch inbox threads (non-archived)
  const { data: inboxData, isLoading: inboxLoading, error: inboxError, refetch: refetchInbox } = useQuery({
    queryKey: ['messages', 'threads', 'inbox'],
    queryFn: async () => {
      const params = new URLSearchParams({
        sort: 'latest',
        // includeArchived is false by default, so only non-archived messages will be returned
      });

      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/list?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data: ThreadsResponse = await response.json();
      return data;
    },
    enabled: currentTab === 'inbox',
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  // Fetch archived threads
  const { data: archivedData, isLoading: archivedLoading, error: archivedError, refetch: refetchArchived } = useQuery({
    queryKey: ['messages', 'threads', 'archived'],
    queryFn: async () => {
      const params = new URLSearchParams({
        sort: 'latest',
        archived: 'true', // Get ONLY archived messages
      });

      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/list?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to fetch archived messages');
      }

      const data: ThreadsResponse = await response.json();
      return data;
    },
    enabled: currentTab === 'archived',
    staleTime: 10000,
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'inbox' | 'archived') => {
    setCurrentTab(newValue);
    // Refetch data when switching tabs
    if (newValue === 'inbox') {
      refetchInbox();
    } else {
      refetchArchived();
    }
  };

  const handleThreadClick = (threadId: number) => {
    navigate(`/messages/${threadId}`);
  };

  const handleNewMessage = () => {
    setNewMessageModalOpen(true);
  };

  const handleNewMessageClose = () => {
    setNewMessageModalOpen(false);
    if (currentTab === 'inbox') {
      refetchInbox(); // Refresh inbox thread list
    }
  };

  const handleRefresh = () => {
    if (currentTab === 'inbox') {
      refetchInbox();
    } else {
      refetchArchived();
    }
  };

  const handleArchiveThread = async (threadId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation to thread
    
    // Optimistically remove the thread from inbox immediately
    queryClient.setQueryData(['messages', 'threads', 'inbox'], (oldData: ThreadsResponse | undefined) => {
      if (!oldData?.data) return oldData;
      return {
        ...oldData,
        data: {
          ...oldData.data,
          threads: oldData.data.threads.filter(thread => thread.id !== threadId)
        }
      };
    });
    
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/archive`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            threadIds: [threadId],
            archive: true
          })
        }
      );

      if (!response?.ok) {
        // If failed, refetch to restore the correct state
        console.error('Failed to archive thread');
        refetchInbox();
      }
    } catch (error) {
      console.error('Error archiving thread:', error);
      // If error, refetch to restore the correct state
      refetchInbox();
    }
  };

  const handleUnarchiveThread = async (threadId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation to thread
    
    // Optimistically remove the thread from archived immediately
    queryClient.setQueryData(['messages', 'threads', 'archived'], (oldData: ThreadsResponse | undefined) => {
      if (!oldData?.data) return oldData;
      return {
        ...oldData,
        data: {
          ...oldData.data,
          threads: oldData.data.threads.filter(thread => thread.id !== threadId)
        }
      };
    });
    
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/archive`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            threadIds: [threadId],
            archive: false
          })
        }
      );

      if (!response?.ok) {
        // If failed, refetch to restore the correct state
        console.error('Failed to unarchive thread');
        refetchArchived();
      }
    } catch (error) {
      console.error('Error unarchiving thread:', error);
      // If error, refetch to restore the correct state
      refetchArchived();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'default';
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <HourglassIcon />; // Waiting on support team
      case 'pending':
        return <WarningIcon />; // Waiting on customer response
      case 'resolved':
        return <CheckCircleIcon />; // Issue resolved
      case 'closed':
        return <LockIcon />; // Thread closed
      default:
        return <MailIcon />;
    }
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // Get the appropriate data based on current tab
  const currentData = currentTab === 'inbox' ? inboxData : archivedData;
  const isLoading = currentTab === 'inbox' ? inboxLoading : archivedLoading;
  const error = currentTab === 'inbox' ? inboxError : archivedError;
  
  // Get all threads from the current tab (no filtering needed)
  const threads = currentData?.data?.threads || [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography
        component="h1"
        sx={{
          color: brandColors.blue,
          fontWeight: 400,
          fontSize: { xs: '2rem', sm: '2.5rem' },
          mb: 1,
        }}
      >
        Messages
      </Typography>
      <Typography
        variant="h6"
        sx={{ color: brandColors.mediumGray, fontWeight: 400, mb: 3 }}
      >
        View and manage your conversations with our support team
      </Typography>

      {/* Tabs and New Message Button */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="message tabs">
            <Tab label="Inbox" value="inbox" />
            <Tab label="Archived" value="archived" />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': {
                    bgcolor: 'action.selected',
                  },
                  width: 40,
                  height: 40,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {currentTab === 'inbox' && (
              <Tooltip title="New Message">
                <IconButton
                  onClick={handleNewMessage}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    width: 40,
                    height: 40,
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Thread List */}
      <Paper>
        {isLoading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">
              Failed to load messages. Please try again later.
            </Alert>
          </Box>
        ) : threads.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No messages found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewMessage}
              sx={{ mt: 2 }}
            >
              Start a Conversation
            </Button>
          </Box>
        ) : (
            <List sx={{ p: 0 }}>
              {threads.map((thread, index) => (
                <ListItemButton
                  key={thread.id}
                  onClick={() => handleThreadClick(thread.id)}
                  divider={index < threads.length - 1}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemAvatar>
                  <Badge
                    badgeContent={thread.unreadCount}
                    color="error"
                    invisible={thread.unreadCount === 0}
                  >
                    <Avatar sx={{ 
                      bgcolor: currentTab === 'archived' ? 'grey.500' :
                               thread.status === 'resolved' ? 'success.main' : 
                               thread.status === 'closed' ? 'grey.500' :
                               thread.status === 'pending' ? 'warning.main' :
                               thread.status === 'open' ? 'grey.600' :
                               'primary.main' 
                    }}>
                      {currentTab === 'archived' ? <ArchiveIcon /> : getStatusIcon(thread.status)}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                
                <ListItemText
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="subtitle1" 
                        component="span"
                      >
                        {thread.subject}
                      </Typography>
                      <Chip
                        label={thread.status}
                        size="small"
                        color={getStatusColor(thread.status) as any}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      {thread.lastMessage && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5 
                          }}
                        >
                          {thread.lastMessage.senderType === 'admin' && (
                            <Chip
                              label="Support"
                              size="small"
                              sx={{ height: 16, fontSize: '0.7rem' }}
                            />
                          )}
                          {truncateContent(thread.lastMessage.content)}
                        </Typography>
                      )}
                      <Typography 
                        variant="caption" 
                        color="text.disabled"
                      >
                        {formatDistanceToNow(new Date(thread.updatedAt), { addSuffix: true })}
                      </Typography>
                    </Box>
                  }
                />
                
                {/* Archive/Unarchive buttons */}
                {currentTab === 'inbox' && (
                  <Tooltip title="Archive thread">
                    <IconButton
                      onClick={(e) => handleArchiveThread(thread.id, e)}
                      size="small"
                      sx={{ 
                        mr: 1,
                        opacity: 0.6,
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: 'action.hover',
                        }
                      }}
                      aria-label="Archive thread"
                    >
                      <ArchiveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                
                {currentTab === 'archived' && (
                  <Tooltip title="Unarchive thread">
                    <IconButton
                      onClick={(e) => handleUnarchiveThread(thread.id, e)}
                      size="small"
                      sx={{ 
                        mr: 1,
                        opacity: 0.6,
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: 'action.hover',
                        }
                      }}
                      aria-label="Unarchive thread"
                    >
                      <UnarchiveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                
                {thread.unreadCount > 0 && (
                  <CircleIcon sx={{ color: 'error.main', fontSize: 12 }} />
                )}
              </ListItemButton>
            ))}
          </List>
        )}
      </Paper>

      {/* New Message Modal */}
      <NewMessageModal
        open={newMessageModalOpen}
        onClose={handleNewMessageClose}
      />
    </Container>
  );
};

export default MessagesPage;